import {
  createElement,
  useRef,
  useEffect,
  forwardRef,
  RefObject,
  useImperativeHandle,
  useCallback,
  useState,
  useReducer,
} from 'react';
import { TreeTableProps, CellProps } from './';
import { BaseTable } from '../Table/__internal__/BaseTable';
import { TablePosition } from '../Table';
import { TreeTableNode } from './TreeTableNode';
import { getTreeTokens } from '../Tree';
import { InvisibleText } from '../InvisibleText';
import { makeSayCell, makeSaySelectionCell } from './__internal__';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { postfixTestId } from '../../util/testId';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import bundle from './NovaComponents-TreeTable-gui-icu.properties';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { SelectionCellContent } from '../Table/__internal__/SelectionCellContent';
import { TreeTableState } from './TreeTableState';

export const DEFAULT_INDENTATION = 20;
const DEFAULT_ICON_OFFSET = 8;
const DEFAULT_ICON_WIDTH = 14;
const DEFAULT_ICON_MARGIN = 4;

const useTextLocalizer = createUseTextLocalizer(bundle);
const useTreeTableId = createUseHtmlId('treeTable');

export const createLabelId = (cellId: string, treeTableId?: string): string => {
  const nodeLabelPrefix = 'tree-node';
  if (treeTableId) {
    return `${nodeLabelPrefix}-${treeTableId}-${cellId}`;
  } else {
    return `${nodeLabelPrefix}-${cellId}`;
  }
};

const CellContent = forwardRef<
  RefObject<any>,
  CellProps & { itemData?: any; suppressTooltip?: boolean }
>((cellProps, ref) => {
  const { hasChildren, level, id } = cellProps.node;
  const isExpanded = cellProps.expandedNodes?.has(id);
  const indent =
    level * DEFAULT_INDENTATION -
    (hasChildren
      ? level === 0
        ? DEFAULT_ICON_OFFSET
        : DEFAULT_ICON_WIDTH + DEFAULT_ICON_MARGIN
      : level === 0
      ? -(DEFAULT_ICON_WIDTH - DEFAULT_ICON_MARGIN)
      : 0);
  const itemData = cellProps.itemData;
  const rowIndex = cellProps.rowIndex;
  const selected = itemData?.preference?.selectAll;
  const hideUnselectedSelectionControl = itemData?.hideUnselectedSelectionControl;
  const [displayState, setDisplayState] = useState<'visible' | 'hidden'>(
    hideUnselectedSelectionControl ? 'hidden' : 'visible'
  );

  const focused =
    itemData?.lastFocusedCell?.rowIndex === rowIndex &&
    itemData?.lastFocusedCell?.colIndex === 0 &&
    itemData?.focusIndicator === 'visible';

  useEffect(() => {
    hideUnselectedSelectionControl && setDisplayState(selected || focused ? 'visible' : 'hidden');
  }, [selected, focused, hideUnselectedSelectionControl]);

  useEffect(() => {
    if (itemData.hideUnselectedSelectionControl === false) setDisplayState('visible');
  }, [itemData.hideUnselectedSelectionControl]);

  const getSelectionControl = (labelledBy?: string) => {
    if (
      itemData &&
      itemData?.hideSelectionControlColumn === true &&
      (itemData.selectionMode === 'parentChild' || itemData.selectionMode === 'multiple')
    )
      return (
        <SelectionCellContent
          rowIndex={cellProps.rowIndex}
          itemData={itemData}
          style={{ visibility: displayState }}
          ariaLabelledBy={labelledBy}
        />
      );
    return undefined;
  };

  const treeNodeContent = (
    <div
      style={{ marginInlineStart: `${indent}px` }}
      onMouseOver={() => {
        hideUnselectedSelectionControl && setDisplayState('visible');
      }}
      onMouseLeave={() => {
        hideUnselectedSelectionControl &&
          setDisplayState(selected || focused ? 'visible' : 'hidden');
      }}
    >
      <TreeTableNode
        testId={cellProps.testId}
        ref={ref}
        expanded={isExpanded}
        node={cellProps.node}
        hasChildren={hasChildren}
        onArrowClick={() => {
          cellProps.onExpand?.(cellProps.node, !isExpanded);
        }}
        suppressTooltip={cellProps.suppressTooltip}
        disabled={cellProps.disabled}
        expandCollapseDisabled={cellProps.isExpandCollapseDisabled?.(
          cellProps.node,
          cellProps.rowKey
        )}
        nodeElement={cellProps.nodeElement}
        selectionControl={getSelectionControl(
          createLabelId(cellProps.node.id, cellProps.treeTableId)
        )}
        labelId={createLabelId(cellProps.node.id, cellProps.treeTableId)}
      />
    </div>
  );
  return cellProps.getNodeContent?.(cellProps.rowIndex, treeNodeContent) ?? treeNodeContent;
});

export const TreeTable = (props: TreeTableProps) => {
  const currentPosition = useRef<TablePosition | undefined>();
  const narratorRef = useRef<any>();
  const localize = useTextLocalizer();
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(getTreeTokens(dt), 'tree');
  const treeTableDescriptionId = useTreeTableId();
  const refGrid = useRef(null);
  const [initialSelectedRows, setInitialSelectedRows] = useState<Set<string>>(new Set());

  const [treeState, dispatch] = useReducer(
    TreeTableState.reducer,
    TreeTableState.init(props.preference?.selectedRows)
  );
  const [interactiveSelection, setInteractiveSelection] = useState(false);
  const [interactiveSelectAll, setInteractiveSelectAll] = useState(false);

  const updateSelection = useCallback(
    (
      selectedRows: ReadonlySet<string> | null | undefined,
      selectAll: boolean | 'mixed' | undefined,
      lastSelectedRowKey?: string,
      selected?: boolean | 'mixed',
      interactive?: boolean
    ): void => {
      if (props.readOnly && interactive) return;
      if (
        (!selectedRows || selectedRows.size === 0) &&
        selectAll !== undefined &&
        !props.hideSelectionControlColumn
      ) {
        props.setSelectAllRows?.({
          selectAll,
          isRowReadOnly: props.isRowReadOnly,
          initialSelectedRows,
        });
      } else if (props.selectionMode === 'parentChild') {
        props.setSelectRows?.({
          selectedRows,
          rowCount: props.rowCount,
          interactive,
          isRowDisabled: props.isRowDisabled,
          isRowReadOnly: props.isRowReadOnly,
          areRowsVisible: props.areRowsVisible,
          initialSelectedRows,
        });
      }
      interactive && props.onSelect?.(selectedRows, selectAll, lastSelectedRowKey, selected);
      setInteractiveSelection(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [treeState.selectedRows]
  );

  useEffect(() => {
    updateSelection(
      treeState.selectedRows,
      props.preference?.selectAll,
      props.preference?.selectionMetadata?.key,
      props.preference?.selectionMetadata?.state,
      interactiveSelection
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeState.selectedRows, treeState.selectAll]);

  useEffect(() => {
    if (!interactiveSelectAll && (treeState.selectAll === false || treeState.selectAll === true))
      props.setSelectAllRows?.({
        selectAll: treeState.selectAll,
        isRowReadOnly: props.isRowReadOnly,
        initialSelectedRows,
      });
    setInteractiveSelectAll(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeState.selectAll]);

  useEffect(() => {
    if (initialSelectedRows?.size > 0 && props.isRowReadOnly) setInitialSelectedRows(new Set());
  }, [initialSelectedRows?.size, props.isRowReadOnly]);

  useImperativeHandle(props.treeTableAPIRef, () => ({
    setSelectedRows: (rowKeys) => {
      if (props.selectionMode === 'parentChild') {
        setInitialSelectedRows(new Set(rowKeys));
        dispatch({
          type: 'parentChildSelection',
          rowKey: rowKeys?.length > 0 ? rowKeys[0] : '',
          selectedRows: new Set(rowKeys),
          selected: true,
          props,
          interactive: false,
        });
      } else if (props.selectionMode !== 'none') {
        const keys: ReadonlySet<string> =
          props.selectionMode === 'single'
            ? new Set([rowKeys?.length > 0 ? rowKeys[0] : ''])
            : new Set(rowKeys);
        props.setSelectRows?.({
          selectedRows: keys,
          isRowReadOnly: props.isRowReadOnly,
          initialSelectedRows,
        });
      }
    },
    setSelectAll: (selectAll) => {
      if (props.selectionMode === 'parentChild') {
        dispatch({
          type: 'parentChildSelection',
          rowKey: '',
          selectedRows: new Set([]),
          selectAll,
          props,
          interactive: false,
        });
      } else {
        dispatch({ type: 'selectRow', selectAll });
      }
    },
    getColumnIndexForOffset: (scrollLeft) => {
      return (refGrid.current as any)?.getColumnIndexForOffset(scrollLeft);
    },
    getRowIndexForOffset(scrollTop) {
      return (refGrid.current as any)?.getRowIndexForOffset(scrollTop);
    },
  }));

  const treeTableSayCell = makeSayCell(
    localize,
    props.selectionMode,
    props.getDataNode,
    props.expandedNodes,
    props.getRowAttributes,
    props.getRowKey,
    props.hideSelectionControlColumn
  );

  const treeTableSaySelectionCell = makeSaySelectionCell(
    localize,
    props.getDataNode,
    props.getRowAttributes,
    props.getRowKey
  );

  useEffect(() => {
    const setFreezeColumnCount = props.setFreezeColumnCount;
    setFreezeColumnCount?.({ count: props.frozenColumnCount ?? 0 });
  }, [props.frozenColumnCount, props.setFreezeColumnCount]);

  useEffect(() => {
    narratorRef.current?.announcePosition();
  }, [props.expandedNodes]);

  const selectionColumnIndex =
    props.disabled ||
    props.hideSelectionControlColumn ||
    (props.selectionMode !== 'multiple' && props.selectionMode !== 'parentChild')
      ? 0
      : 1;

  return (
    <div
      style={tokens}
      onKeyDown={(event: React.KeyboardEvent): void => {
        if (event.key === 'Enter' && event.ctrlKey) {
          if (currentPosition.current) {
            const node = props.getDataNode
              ? props.getDataNode(currentPosition.current.rowIndex)
              : undefined;
            if (
              node &&
              props.isExpandCollapseDisabled?.(
                node,
                props.getRowKey?.(currentPosition.current.rowIndex)
              )
            )
              return;
            const treeColIndex = selectionColumnIndex;
            if (node && currentPosition.current.colIndex === treeColIndex) {
              const expanded = props.expandedNodes.has(node.id);
              props.onExpand?.(node, !expanded);
            }
          }
        }
      }}
    >
      {props.role !== 'grid' && (
        <InvisibleText
          testId={postfixTestId(props.testId, 'describedby-text')}
          id={treeTableDescriptionId}
        >
          {localize('treeTable.msg')}
        </InvisibleText>
      )}
      <BaseTable
        {...props}
        internalRef={refGrid}
        disableOnDoubleClickKeyboardActivation
        ariaDescribedBy={
          props.role === 'grid'
            ? props.ariaDescribedBy
            : props.ariaDescribedBy
            ? `${props.ariaDescribedBy} ${treeTableDescriptionId}`
            : treeTableDescriptionId
        }
        addRowAttributes={(rowIndex) => {
          const node = props.getDataNode?.(rowIndex);
          const rowKey = props.getRowKey?.(rowIndex);
          if (!node || !rowKey) return {};
          const rowAttributes = props.getRowAttributes?.(rowKey);

          return {
            ...(rowAttributes?.level && { 'aria-level': rowAttributes.level }),
            'aria-rowindex': rowIndex + 1,
          };
        }}
        addCellAttributes={(rowIndex, columnIndex) => {
          if (rowIndex > -1) {
            // If selectionMode of undefined is passed to the BaseTable it defaults to 'multiple'
            const hasSelectionColumn =
              props.selectionMode === 'parentChild' ||
              props.selectionMode === 'multiple' ||
              props.selectionMode === undefined;
            const node = props.getDataNode?.(rowIndex);
            const firstColumn = hasSelectionColumn ? 1 : 0;

            if (columnIndex === firstColumn && node) {
              const isExpanded = node.hasChildren ? props.expandedNodes?.has(node.id) : undefined;
              return {
                'aria-expanded': isExpanded,
              };
            }
          }
          return {};
        }}
        role={props.role ?? 'treegrid'}
        narratorRef={narratorRef}
        setSelectRow={(selectionProps) => {
          if (props.readOnly) return;
          const isParentChild = props.selectionMode === 'parentChild';
          props.setSelectRow?.({
            ...selectionProps,
            clearPrevious: isParentChild ? false : selectionProps.clearPrevious,
            rowCount: props.rowCount,
            isRowDisabled: props.isRowDisabled,
            isRowReadOnly: props.isRowReadOnly,
            hideSelectAll: props.hideSelectAll || props.hideSelectionControlColumn,
            columnHeadersVisible: props.columnHeadersVisible,
            areRowsVisible: props.areRowsVisible,
            interactive: true,
          });
        }}
        setSelectAllRows={(selectionProps) => {
          if (props.readOnly) return;
          props.setSelectAllRows?.(selectionProps);
        }}
        selectionMode={props.selectionMode === 'parentChild' ? 'multiple' : props.selectionMode}
        selectAll={props.selectAll}
        getSelectAllCheckboxState={() => {
          const state = props.getSelectAllCheckboxState?.();
          if (state !== undefined || props.selectionMode !== 'parentChild' || !interactiveSelection)
            return state;
          return treeState?.selectAll || props.preference?.selectAll;
        }}
        getSelectionCheckboxState={(rowKey) => {
          const readOnly = props.isRowReadOnly?.(rowKey);
          if (readOnly) {
            const { selectedRows, selectAll } = treeState;
            if (selectedRows?.has(rowKey) || selectAll === true) return true;
          }
          const state = props.getSelectionCheckboxState?.(rowKey);
          if (state !== undefined) return state;
          const contains = treeState.partiallySelectedRows?.includes(rowKey);
          return contains === true ? 'mixed' : undefined;
        }}
        isRowReadOnly={(key) => {
          const readOnly = props.isRowReadOnly?.(key) || false;
          if (readOnly && treeState.selectAll === true && !initialSelectedRows.has(key)) {
            initialSelectedRows.add(key);
            setInitialSelectedRows(initialSelectedRows);
          }
          return props.isRowReadOnly?.(key) || false;
        }}
        unmanagedColumnKeys={props.columns && props.columns[0] ? [props.columns[0].key] : undefined}
        renderCell={({ rowIndex, columnKey, columnIndex, disabled, ref, itemData }) => {
          const node = props.getDataNode?.(rowIndex);
          if (columnIndex === 0 && node) {
            const rowKey = props.getRowKey?.(rowIndex);
            return (
              <CellContent
                testId={postfixTestId(props.testId, 'cellContent')}
                treeTableId={props.id}
                ref={ref}
                rowIndex={rowIndex}
                rowKey={rowKey}
                node={node}
                isExpandCollapseDisabled={props.isExpandCollapseDisabled}
                expandedNodes={props.expandedNodes}
                onExpand={props.onExpand}
                disabled={disabled}
                getNodeContent={props.getDataNodeContainer}
                nodeElement={props.getNodeElement?.(rowKey)}
                itemData={itemData}
                suppressTooltip={props.suppressNodeTooltip}
              />
            );
          }
          return props.renderCellData({
            columnIndex,
            rowIndex,
            columnKey,
            disabled,
          });
        }}
        onSelect={(selectedRows, selectAll, lastSelectedRowKey, lastSelectedRowState) => {
          setInteractiveSelection(true);
          if (props.selectionMode !== 'parentChild') {
            dispatch({ type: 'selectRow', selectedRows, selectAll, interactive: true });
          } else {
            if (selectAll === true || selectAll === false) setInteractiveSelectAll(true);
            dispatch({
              type: 'parentChildSelection',
              rowKey: lastSelectedRowKey || '',
              selectedRows,
              selectAll,
              selected: lastSelectedRowState,
              props,
              interactive: true,
              initialSelectedRows,
            });
          }
        }}
        onFocusChange={(position: TablePosition) => {
          currentPosition.current = position;
          props.onFocusChange?.(position);
        }}
        narration={{
          onSayCell: treeTableSayCell,
          onSaySelectionCell: treeTableSaySelectionCell,
          disableRowChangeNarration: true,
        }}
      />
    </div>
  );
};
